import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './asset-viewer.style.js';
import { PhotoUploadService } from '../../services/photo-upload.service.js';
import { 
  getLocationsByCompany, 
  getLocations, 
  updateAsset, 
  getPartsByAsset
} from '@cmms/api';
import { db } from '../../services/auth.service.js';
import '../../work-orders/work-order-viewer/work-order-viewer.js';
import '../../pm/pm-viewer/pm-viewer.js';
import '../../parts/part-viewer/part-viewer.js';
import './parts/index.js';

@customElement('asset-viewer')
export class AssetViewer extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Object }) asset: any = null;
  @property({ type: String }) assetId: string = '';
  @property({ type: String }) companyId: string = '';

  @state() private activeTab = 'info';
  @state() private partsLoading = false;
  @state() private partsError: string | null = null;
  @state() private parts: Array<any> = [];
  @state() private isUploadingPhoto = false;
  @state() private photoUploadError = '';
  @state() private locations: Array<{ id: string; altId?: string; name: string }> = [];
  @state() private hasChanges = false;

  private photoUploadService = new PhotoUploadService();

  connectedCallback() {
    super.connectedCallback();
    this.loadLocations();
    this.addEventListener('form-changed', this.handleFormChanged);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('form-changed', this.handleFormChanged);
  }

  private handleFormChanged = (event: Event) => {
    const customEvent = event as CustomEvent;
    this.hasChanges = customEvent.detail.hasChanges;
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeTab')) {
      if (this.activeTab === 'parts') this.loadParts();
    }
    if (changed.has('asset') && this.asset) {
      // Reload locations when asset changes to ensure they're available
      this.loadLocations();
    }
  }


  private async loadLocations() {
    try {
      let result: any;
      if (this.companyId) {
        result = await getLocationsByCompany(db, this.companyId);
      }
      if (!result || !result.data || result.data.length === 0) {
        result = await getLocations(db);
      }
      const raw = (result?.data ?? []) as any[];
      
      this.locations = raw.map((l: any) => ({
        id: l.id || l.$id || l.location_id,
        altId: l.location_id || l.model?.location_id,
        name: l.name || l.model?.name || 'Unknown'
      }));
    } catch (error) {
      // Silent fail - locations will remain empty
    }
  }


  static styles = styles;

  // Photo upload
  private handlePhotoClick() {
    (this.shadowRoot?.querySelector('.photo-file-input') as HTMLElement)?.click();
  }


  private openPart(part: any) {
    const viewer = document.createElement('part-viewer') as any;
    viewer.open = true;
    viewer.part = part;
    document.body.appendChild(viewer);
    viewer.addEventListener('modal-closed', () => document.body.removeChild(viewer), { once: true });
  }

  private async loadParts() {
    const ids = Array.from(new Set([
      this.asset?.id,
      this.asset?.asset_id,
      this.asset?.model?.asset_id
    ].filter(Boolean).map((v: any) => String(v))));
    if (ids.length === 0) { this.parts = []; return; }
    try {
      this.partsLoading = true; this.partsError = null;
      const collected: any[] = [];
      for (const id of ids) {
        const resp: any = await getPartsByAsset(db, id, this.companyId);
        const raw = Array.isArray(resp) ? resp : (resp?.data || []);
        if (raw && raw.length) collected.push(...raw);
      }
      // Normalize shape
      this.parts = collected.map((part: any) => ({
        id: part.id || part.$id,
        name: part.name || part.model?.name || 'Unnamed',
        stock_qty: part.stock_qty ?? part.model?.stock_qty ?? 0,
        min_qty: part.min_qty ?? part.model?.min_qty ?? 0,
        asset_id: part.asset_id || part.model?.asset_id || ''
      }));
    } catch (e: any) {
      this.partsError = e?.message || 'Failed to load parts';
      this.parts = [];
    } finally {
      this.partsLoading = false;
    }
  }

  private async handleTabChange(event: any) {
    this.activeTab = event.detail.name;
    if (event.detail.name === 'parts') {
      await this.loadParts();
    }
  }
  private async handlePhotoFileSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files[0]) await this.uploadPhoto(input.files[0]);
  }
  private async uploadPhoto(file: File) {
    const firestoreId = this.asset?.id ?? this.asset?.asset_id;
    const assetId = this.asset?.asset_id ?? this.asset?.id;
    if (!firestoreId || !assetId) { this.photoUploadError = 'Asset ID not found'; return; }

    try {
      this.isUploadingPhoto = true;
      this.photoUploadError = '';

      // If asset already has a photo, delete the old one first
      if (this.asset?.photo_url) {
        try {
          console.log('Deleting old photo before uploading new one...');
          const deleteResult = await this.photoUploadService.deleteAssetPhoto(assetId, 'default', this.asset.photo_url);
          if (!deleteResult.success) {
            console.warn('Failed to delete old photo:', deleteResult.error);
            // Continue with upload even if deletion fails
          }
        } catch (error) {
          console.warn('Error deleting old photo:', error);
          // Continue with upload even if deletion fails
        }
      }

      const result = await this.photoUploadService.uploadPhoto(file, assetId, 'default');
      if (result.success && result.url) {
        try {
          const updateResult = await updateAsset(db, firestoreId, { photo_url: result.url, updated_at: Date.now() });
          if ((updateResult as any)?.error) { this.photoUploadError = 'Photo uploaded but failed to update database'; return; }
        } catch {
          this.photoUploadError = 'Photo uploaded but failed to update database';
          return;
        }

        // Update the local asset object to trigger re-render
        this.asset = { ...this.asset, photo_url: result.url };
        
        // Dispatch event for parent components
        this.dispatchEvent(new CustomEvent('asset-updated', {
          detail: { asset: this.asset }, bubbles: true, composed: true
        }));
      } else {
        this.photoUploadError = (result as any).error || 'Failed to upload photo';
      }
    } catch (error: any) {
      this.photoUploadError = error?.message || 'Failed to upload photo';
    } finally {
      this.isUploadingPhoto = false;
      const input = this.shadowRoot?.querySelector('.photo-file-input') as HTMLInputElement;
      if (input) input.value = '';
    }
  }

  // Close helpers
  private closeOnly = () => {
    this.activeTab = 'info';
    this.isUploadingPhoto = false;
    this.photoUploadError = '';
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
    this.open = false;
  };
  private onDialogAfterHide = (e: Event) => {
    const dialogEl = this.shadowRoot?.querySelector('sl-dialog');
    if (e.target !== dialogEl) return;
    this.closeOnly();
    this.remove();
  };

  // Edit / Save / Done
  private handleEdit() {
    const assetInfoComponent = this.shadowRoot?.querySelector('asset-info') as any;
    if (assetInfoComponent) {
      assetInfoComponent.isEditing = true;
    }
  }

  private handleCancel() {
    const assetInfoComponent = this.shadowRoot?.querySelector('asset-info') as any;
    if (assetInfoComponent) {
      assetInfoComponent.handleCancel();
    }
    this.hasChanges = false;
  }

  private async handleSave() {
    const assetInfoComponent = this.shadowRoot?.querySelector('asset-info') as any;
    if (assetInfoComponent) {
      await assetInfoComponent.handleSave();
      this.hasChanges = false;
    }
  }

  private async handleDone() {
    if (this.hasChanges) {
      await this.handleSave();
    }
    this.closeOnly();
    this.remove();
  }

  private checkForChanges() {
    const assetInfoComponent = this.shadowRoot?.querySelector('asset-info') as any;
    if (assetInfoComponent) {
      this.hasChanges = assetInfoComponent.isDirty();
    }
  }

  private downloadQRCode() {
    if (this.asset?.qr_code_url) {
      const link = document.createElement('a');
      link.href = this.asset.qr_code_url;
      link.download = `asset-${this.asset.id || this.asset.name}-qr-code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }


  render() {
    if (!this.asset) return html``;

    const assetName = this.asset.name || '';
    const assetParent = this.asset.parent_name || 'Root Asset';

    return html`
      <sl-dialog
        label="Asset Viewer"
        .open=${this.open}
        @sl-after-hide=${this.onDialogAfterHide}
      >
        <div class="asset-info">
          <div class="asset-image-placeholder ${this.isUploadingPhoto ? 'uploading' : ''}" @click=${this.handlePhotoClick}>
            ${this.asset.photo_url ? html`
              <img src="${this.asset.photo_url}" alt="Asset photo" />
              <div class="upload-overlay"><sl-icon name="camera"></sl-icon></div>
            ` : html`<sl-icon name="camera" size="large" style="color: var(--sl-color-neutral-400);"></sl-icon>`}
          </div>

          <div class="asset-details">
            <div class="asset-name">${assetName}</div>
            <div class="asset-location">${assetParent}</div>
            <div class="asset-location">${this.assetId}</div>
          </div>

          ${this.asset?.qr_code_url ? html`
            <div class="header-qr-code">
              <img 
                src="${this.asset.qr_code_url}" 
                alt="QR Code for ${this.asset.name}"
                style="width: 100px; height: 100px; border: 1px solid var(--sl-color-neutral-300); border-radius: 8px;"
              />
              <sl-button 
                
                size="medium"
                circle
                @click=${this.downloadQRCode}
                title="Download QR Code"
              >
                <sl-icon name="download"></sl-icon>
              </sl-button>
            </div>
          ` : ''}
        </div>

        <sl-tab-group @sl-tab-show=${this.handleTabChange}>
          <sl-tab slot="nav" panel="info">
            <sl-icon name="info-circle"></sl-icon> Info
          </sl-tab>
          <sl-tab slot="nav" panel="pms">
            <sl-icon name="wrench"></sl-icon> PMs
          </sl-tab>
          <sl-tab slot="nav" panel="wos">
            <sl-icon name="file-text"></sl-icon> WOs
          </sl-tab>
          <sl-tab slot="nav" panel="parts">
            <sl-icon name="gear"></sl-icon> Parts
          </sl-tab>
          <sl-tab slot="nav" panel="downtimes">
            <sl-icon name="clock"></sl-icon> Downtimes
          </sl-tab>
          <sl-tab slot="nav" panel="reports" disabled>
            <sl-icon name="bar-chart"></sl-icon> Reports
          </sl-tab>

          <sl-tab-panel name="info">
            <asset-info 
              .asset=${this.asset}
              .companyId=${this.companyId}
              .locations=${this.locations}>
            </asset-info>
          </sl-tab-panel>

          <sl-tab-panel name="wos">
            <asset-wos 
              .asset=${this.asset}
              .companyId=${this.companyId}>
            </asset-wos>
          </sl-tab-panel>

          <sl-tab-panel name="pms">
            <asset-pms 
              .asset=${this.asset}
              .companyId=${this.companyId}>
            </asset-pms>
          </sl-tab-panel>

          <sl-tab-panel name="parts">
          <div class="info-description">Parts linked to this asset</div>
          ${this.partsLoading ? html`
            <div style="display:flex;justify-content:center;padding:1rem"><sl-spinner></sl-spinner></div>
          ` : this.partsError ? html`
            <sl-alert variant="danger" open>${this.partsError}</sl-alert>
          ` : this.parts.length === 0 ? html`
            <div class="field-row" style="justify-content:center;color:var(--sl-color-neutral-600)">No parts linked to this asset</div>
          ` : html`
            <div class="data-table-container">
              <table class="data-table" style="width:100%">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Stock</th>
                    <th>Min</th>
                  </tr>
                </thead>
                <tbody>
                  ${this.parts.map((part: any) => html`
                    <tr style="cursor:pointer" @click=${() => this.openPart(part)}>
                      <td>${part.name}</td>
                      <td>${part.stock_qty}</td>
                      <td>${part.min_qty}</td>
                    </tr>
                  `)}
                </tbody>
              </table>
            </div>
          `}
          </sl-tab-panel>

          <sl-tab-panel name="downtimes">
            <asset-downtimes .asset=${this.asset} .companyId=${this.companyId}></asset-downtimes>
          </sl-tab-panel>

          <sl-tab-panel name="reports">
            <div class="info-description">Asset Reports - Coming Soon</div>
            <div class="field-row" style="justify-content:center;color:var(--sl-color-neutral-600)">Reports functionality will be available soon</div>
          </sl-tab-panel>
        </sl-tab-group>

        <!-- Hidden file input for photo upload -->
        <input type="file" class="photo-file-input" accept="image/*" @change=${this.handlePhotoFileSelect} style="display:none;" />

        <!-- Footer: Edit/Done buttons like user viewer -->
        <div slot="footer" class="dialog-footer">
          <div>
            ${this.photoUploadError ? html`<sl-alert variant="warning" open>${this.photoUploadError}</sl-alert>` : null}
          </div>
          ${(() => {
            const assetInfoComponent = this.shadowRoot?.querySelector('asset-info') as any;
            const isEditing = assetInfoComponent?.isEditing || false;
            
            return isEditing ? html`
              <sl-button
                variant="neutral"
                size="large"
                @click=${this.handleCancel}>
                Cancel
              </sl-button>
              <sl-button
                variant="primary"
                size="large"
                @click=${this.handleSave}
                ?loading=${assetInfoComponent?.isSaving || false}>
                Save
              </sl-button>
            ` : html`
              <sl-button
                variant="neutral"
                size="large"
                @click=${this.handleEdit}>
                Edit
              </sl-button>
              <sl-button
                variant="primary"
                size="large"
                @click=${this.handleDone}>
                ${this.hasChanges ? 'Save & Close' : 'Done'}
              </sl-button>
            `;
          })()}
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asset-viewer': AssetViewer;
  }
}
